import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ExternalLink, Loader2, Search, ListChecks } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { STATE_NAMES } from '../data/usStates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEST_POLICY_LABELS = {
  A: 'Always required', F: 'Test flexible', I: 'Tests ignored',
  N: 'Never required', S: 'Sometimes required',
};

export const COLLECTIONS = {
  'no-application-fee': {
    params: { free_app: 'true' },
    eyebrow: 'Free to apply',
    h1a: 'Colleges with', h1b: 'no application fee',
    title: 'Colleges With No Application Fee 2025-26 — 590+ Free Common App Schools',
    description: 'The complete list of Common App colleges charging $0 to apply in 2025-26. Every free-application school with its ED, EA, and rolling deadlines and test policy.',
    keywords: 'colleges with no application fee, free application colleges 2026, no fee common app schools, colleges free to apply, application fee waiver',
    intro: 'Application fees add up fast — $70-$90 per school is common at selective colleges. Every school below charges $0 through the Common App, no fee waiver needed. Deadlines and test policies shown are from the official Common App 2025-26 grid.',
    faqs: [
      { q: 'Are these colleges really free to apply to?', a: 'Yes. Every school on this list reports a $0 US application fee on the official Common App 2025-26 requirements grid. Some colleges may charge separately for international applicants — check the fee column on each school\u2019s detail page.' },
      { q: 'Does applying to a free college hurt my chances?', a: 'No. Application fees have nothing to do with selectivity or quality. Many strong colleges waive fees to reduce barriers and grow their applicant pools. Admissions readers never see whether you paid a fee.' },
      { q: 'What if my top school still charges a fee?', a: 'Ask about fee waivers. Students with financial need qualify for Common App fee waivers, and many colleges grant them for campus visits, virtual sessions, or simply on request to your regional admissions officer.' },
      { q: 'How many colleges should I apply to if fees are not a constraint?', a: 'Quality beats quantity. A balanced list of 8-12 schools — safeties, targets, and reaches — outperforms 20 rushed applications, even when the applications are free. Every supplemental essay still costs time.' },
    ],
  },
  'test-optional': {
    params: { test_policy: 'N,I' },
    eyebrow: 'No SAT/ACT required',
    h1a: 'Test-optional colleges,', h1b: 'the full list',
    title: 'Test-Optional Colleges 2025-26 — 580 Schools That Never Require the SAT/ACT',
    description: 'Every Common App college where tests are never required or ignored entirely in 2025-26. Full list with deadlines, fees, and each school\u2019s exact testing policy.',
    keywords: 'test optional colleges 2026, colleges that dont require sat, test blind colleges list, no sat colleges, test free colleges 2025-26',
    intro: 'This list includes every Common App college whose official 2025-26 policy is "never required" (N) or "ignored if submitted" (I). Schools marked I are test-blind — they will not look at scores even if you send them. Another 313 schools are test-flexible (S/F); check any school\u2019s detail page for its exact policy.',
    faqs: [
      { q: 'What is the difference between test-optional and test-blind?', a: 'Test-optional (N — never required) means you choose whether to submit scores, and strong scores can still help. Test-blind (I — ignored) means the college will not consider scores at all, even if submitted.' },
      { q: 'Should I still submit my SAT/ACT to a test-optional college?', a: 'If your score is at or above the college\u2019s middle 50% range, submitting usually helps — it can also unlock merit scholarships. Below the 25th percentile, most consultants advise withholding.' },
      { q: 'Are the Ivies test-optional in 2025-26?', a: 'Mostly no. Harvard, Yale, Dartmouth, Brown, and Cornell (most colleges) have reinstated testing requirements, shown as policy A on our grid. Columbia remains permanently test-optional. Always verify on the school\u2019s detail page.' },
      { q: 'Can a test-optional application still be competitive at selective schools?', a: 'Yes — but the rest of your application carries more weight. Grades, rigor, essays, and recommendations must independently prove academic readiness. A diagnostic can tell you where you stand.' },
    ],
  },
  'rolling-admissions': {
    params: { rolling_admission: 'true' },
    eyebrow: 'Apply year-round',
    h1a: 'Colleges with', h1b: 'rolling admissions',
    title: 'Colleges With Rolling Admissions 2025-26 — 600+ Schools, Full List',
    description: 'All 600+ Common App colleges using rolling admissions in 2025-26 — decisions in weeks, not months. Full list with fees, test policies, and early deadlines.',
    keywords: 'colleges with rolling admissions, rolling admission colleges 2026, colleges still accepting applications, late application colleges, rolling deadline universities',
    intro: 'Rolling admissions means the college reviews applications as they arrive and returns decisions in weeks — some in days. It is the single best safety-net strategy in admissions: apply early in the cycle for the best odds at housing, scholarships, and capacity-limited majors.',
    faqs: [
      { q: 'How fast do rolling admissions decisions come back?', a: 'Typically 2-8 weeks after your file is complete, and some large publics respond in under two weeks. The earlier in the cycle you apply, the faster and more favorable the review tends to be.' },
      { q: 'Is it harder to get in later in a rolling cycle?', a: 'Often, yes. Seats, scholarships, and popular majors (nursing, engineering, business) fill as the cycle progresses. Treat priority dates as real deadlines even when the college technically accepts applications year-round.' },
      { q: 'Can I use a rolling school as a safety while applying ED elsewhere?', a: 'Yes — this is the classic strategy. A rolling acceptance in October removes the fear from your November ED decision. Rolling admissions are non-binding, so there is no conflict with an ED agreement.' },
      { q: 'Do rolling colleges still have scholarship deadlines?', a: 'Almost always. Merit-aid consideration frequently requires applying by a priority date (often November 1 - December 1) even when admission itself stays open. Check each school\u2019s detail page and website.' },
    ],
  },
  'early-decision-2': {
    params: { has_edii: 'true' },
    eyebrow: 'The January second chance',
    h1a: 'Every college with', h1b: 'Early Decision II',
    title: 'Early Decision II Colleges — All 122 Schools With ED II Deadlines 2025-26',
    description: 'The complete list of 122 Common App colleges offering Early Decision II in 2025-26, with exact EDII deadlines. The most underused strategic lever in admissions.',
    keywords: 'early decision 2 colleges, ED II colleges list, edii deadlines 2026, colleges with ed2, early decision ii schools',
    intro: 'Early Decision II is the same binding commitment as ED I with a January deadline — and it is the strongest play for students deferred or denied in the early round, or whose applications got meaningfully stronger by winter. These 122 colleges offer it; most families never realize how many do.',
    faqs: [
      { q: 'Is Early Decision II binding like ED I?', a: 'Yes — identical contract. If admitted, you enroll and withdraw all other applications. The only difference is timing: EDII deadlines cluster around January 1-15, with decisions in February.' },
      { q: 'Who should apply Early Decision II?', a: 'Three profiles: students deferred/denied from their ED I or REA school who have a clear second favorite; students whose fall-semester grades or new achievements materially strengthen their file; and students who simply were not ready by November 1.' },
      { q: 'Does EDII carry the same acceptance-rate advantage as ED I?', a: 'Usually a smaller but still real boost. Colleges use EDII to lock in yield after seeing their early results, so demonstrated commitment continues to carry weight — especially at ED-dependent liberal arts colleges.' },
      { q: 'Can I apply EDII somewhere while waiting on other RD decisions?', a: 'Yes. You may hold pending Regular Decision applications while applying EDII — but if the EDII school admits you, you must withdraw them all. Do not apply EDII unless you would genuinely attend.' },
    ],
  },
  'early-action': {
    params: { has_ea: 'true' },
    eyebrow: 'Early answers, no strings',
    h1a: 'Every college with', h1b: 'Early Action',
    title: 'Early Action Colleges — All 446 Schools With EA Deadlines 2025-26',
    description: 'The complete list of 446 Common App colleges offering non-binding Early Action in 2025-26, with exact EA deadlines, fees, and test policies.',
    keywords: 'early action colleges list, EA deadlines 2025-26, non binding early application, early action vs early decision, colleges with early action',
    intro: 'Early Action gets you a decision months early with zero obligation — the default smart strategy for strong applicants. Most EA deadlines land November 1-15. Six schools use Restrictive Early Action (Harvard, Yale, Princeton, Stanford among them), which limits where else you can apply early.',
    faqs: [
      { q: 'Is Early Action binding?', a: 'No. EA is completely non-binding — you get an early answer, keep applying elsewhere, compare financial-aid offers, and have until May 1 to decide. That is what separates it from Early Decision.' },
      { q: 'Does Early Action improve my admission odds?', a: 'Modestly, at most schools. The bigger advantages are practical: earlier merit-scholarship consideration, priority for honors programs and housing, and the psychological safety of October/December acceptances.' },
      { q: 'What is Restrictive Early Action (REA)?', a: 'A single-choice variant used by six ultra-selective schools: non-binding, but you generally may not apply early to other private colleges. It signals first-choice interest where ED does not exist.' },
      { q: 'Can I apply Early Action to multiple colleges?', a: 'Yes — unrestricted EA has no limit, and stacking several EA applications is standard strategy. The exception is REA/single-choice EA schools, which restrict simultaneous early applications to private colleges.' },
    ],
  },
};

function Cell({ value, muted = false }) {
  if (!value || value === 'None') return <span className="text-[#C9C9C0]">—</span>;
  if (value === 'See Website') return <span className="text-[11px] text-muted-foreground italic">See site</span>;
  return <span className={`text-[12px] ${muted ? 'text-muted-foreground' : 'text-foreground'}`}>{value}</span>;
}

export default function DeadlinesCollectionPage({ collectionKey }) {
  const navigate = useNavigate();
  const cfg = COLLECTIONS[collectionKey];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setQuery('');
    const params = new URLSearchParams({ ...cfg.params, limit: '2000' });
    fetch(`${API_URL}/api/common-app/requirements?${params.toString()}`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setItems(d.items || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [collectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.trim().toLowerCase();
    return items.filter((r) => r.name.toLowerCase().includes(q));
  }, [items, query]);

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Common App Deadlines Grid", "item": "https://ivyleagueconsulting.org/deadlines-grid" },
        { "@type": "ListItem", "position": 2, "name": cfg.title.split(' — ')[0], "item": `https://ivyleagueconsulting.org/deadlines-grid/${collectionKey}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": cfg.faqs.map((f) => ({
        "@type": "Question", "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": cfg.title.split(' — ')[0],
      "numberOfItems": items.length,
      "itemListElement": items.slice(0, 50).map((r, i) => ({
        "@type": "ListItem", "position": i + 1, "name": r.name,
        "url": `https://ivyleagueconsulting.org/deadlines-grid/${r.slug}`,
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid={`collection-page-${collectionKey}`}>
      <SEO title={cfg.title} description={cfg.description} keywords={cfg.keywords} schema={schema} />
      <MarketingNav />

      {/* Breadcrumb */}
      <div className="pt-20 pb-4 bg-[#F9F9F7] border-b border-[#E2E2E2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/deadlines-grid" className="text-xs text-muted-foreground hover:text-[#0F392B] inline-flex items-center gap-1.5" data-testid="collection-breadcrumb-back">
            <ArrowLeft className="h-3 w-3" /> Common App grid
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 md:py-16 bg-[#0F392B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3 flex items-center gap-2">
            <ListChecks className="h-4 w-4" /> {cfg.eyebrow} · 2025-26
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1] mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="collection-title">
            {cfg.h1a} <span className="italic text-[#D4AF37]">{cfg.h1b}</span>
          </h1>
          <p className="text-base text-white/70 max-w-3xl leading-relaxed" data-testid="collection-intro">{cfg.intro}</p>
          {!loading && (
            <p className="mt-6 text-2xl font-semibold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="collection-count">
              {items.length.toLocaleString()} <span className="text-sm text-white/60 font-normal uppercase tracking-widest">colleges on this list</span>
            </p>
          )}
        </div>
      </section>

      {/* Table */}
      <section className="py-12 md:py-16" data-testid="collection-directory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search this list…"
                className="pl-9 h-11 bg-white border-[#E2E2E2] text-sm"
                data-testid="collection-search"
              />
            </div>
            <p className="text-xs text-muted-foreground" data-testid="collection-showing">
              Showing <span className="text-[#0F392B] font-semibold">{filtered.length.toLocaleString()}</span> of {items.length.toLocaleString()} colleges
            </p>
          </div>

          <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading colleges…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16" data-testid="collection-empty">
                <p className="text-base text-[#0F392B] font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>No colleges match that search.</p>
                <Button onClick={() => setQuery('')} variant="outline" className="border-[#0F392B] text-[#0F392B]">Clear search</Button>
              </div>
            ) : (
              <table className="w-full text-sm min-w-[900px]" data-testid="collection-table">
                <thead>
                  <tr className="border-b border-[#E2E2E2] bg-[#F9F9F7]">
                    <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">College</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">State</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">ED</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">EDII</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">EA</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">RD / Rolling</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Fee (US)</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold" title="Test Policy">Tests</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={r.slug} className={`border-b border-[#E2E2E2] last:border-b-0 hover:bg-[#F9F9F7]/60 cursor-pointer ${i % 2 === 1 ? 'bg-[#F9F9F7]/30' : ''}`} data-testid={`collection-row-${r.slug}`} onClick={() => navigate(`/deadlines-grid/${r.slug}`)}>
                      <td className="py-3 px-4">
                        <p className="text-[13px] font-semibold text-[#0F392B] hover:underline" style={{ fontFamily: "'Playfair Display', serif" }}>{r.name}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{r.school_type || 'Coed'}</p>
                      </td>
                      <td className="py-3 px-3"><Cell value={r.state ? (STATE_NAMES[r.state] || r.state) : null} muted /></td>
                      <td className="py-3 px-3"><Cell value={r.ed} /></td>
                      <td className="py-3 px-3"><Cell value={r.edii} /></td>
                      <td className="py-3 px-3"><Cell value={r.ea} /></td>
                      <td className="py-3 px-3"><Cell value={r.rd_rolling} /></td>
                      <td className="py-3 px-3"><Cell value={r.fee_us} /></td>
                      <td className="py-3 px-3" title={TEST_POLICY_LABELS[r.test_policy] || ''}>
                        {r.test_policy ? (
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                            r.test_policy === 'A' ? 'bg-[#0F392B] text-white' :
                            r.test_policy === 'N' || r.test_policy === 'I' ? 'bg-[#F9F9F7] text-[#0F392B] border border-[#E2E2E2]' :
                            'bg-[#D4AF37]/20 text-[#0F392B]'
                          }`}>{r.test_policy}</span>
                        ) : <span className="text-[#C9C9C0]">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground text-center italic mt-6">
            Source: Common Application 2025-26 first-year grid, updated Oct 24 2025. Always verify with each college before submitting.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-[#F9F9F7] border-y border-[#E2E2E2]" data-testid="collection-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-medium text-[#0F392B] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {cfg.faqs.map((f, i) => (
              <div key={i} className="bg-white border border-[#E2E2E2] rounded-2xl p-6" data-testid={`collection-faq-${i}`}>
                <p className="text-sm font-semibold text-[#0F392B] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{f.q}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other collections */}
      <section className="py-14" data-testid="collection-others">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl font-medium text-[#0F392B] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            More curated lists
          </h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(COLLECTIONS).filter(([k]) => k !== collectionKey).map(([k, c]) => (
              <Link key={k} to={`/deadlines-grid/${k}`} className="px-4 py-2 rounded-full text-xs font-medium bg-white text-[#0F392B] border border-[#E2E2E2] hover:border-[#0F392B]/40 transition-all" data-testid={`collection-link-${k}`}>
                {c.h1a} {c.h1b} →
              </Link>
            ))}
            <Link to="/deadlines-grid" className="px-4 py-2 rounded-full text-xs font-medium bg-[#0F392B] text-white border border-[#0F392B] transition-all" data-testid="collection-link-grid">
              Full grid — 1,105 colleges →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F392B]" data-testid="collection-cta-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Build your list around data, not guesswork.
          </h2>
          <p className="text-sm text-white/70 mb-8 max-w-md mx-auto">
            Add any school on this page to your portal and its deadlines, fees, and test policy auto-sync — no re-entry.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/sign-up">
              <Button size="lg" className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-12 px-8 font-semibold shadow-xl" data-testid="collection-cta-platform">
                Start the Platform — $99 <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/diagnostic">
              <Button size="lg" variant="outline" className="h-12 px-8 border-white/40 text-white hover:bg-white hover:text-[#0F392B]" data-testid="collection-cta-diagnostic">
                Take the Diagnostic <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
